import React from 'react';

import classnames from 'classnames';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { noop } from 'ui-components/default-props/noop';
import { Text, TextType } from 'ui-components/components/text';

import { OrderItemPropType } from '../../prop-types/order-item';
import { ReturnReasonService } from '../../services/return-reason-service';
import ReturnReasonDropdown from '../return-reason-dropdown';
import { ItemDisplayImage } from './item-display-image';
import { ItemDisplayDescriptions } from './item-display-descriptions';
import { ItemDisplayPriceWrapper } from './item-display-price-wrapper';
import { useProductCatalogFeatureGate } from '../../feature-gates/use-product-catalog-feature-gate';

import style from './item-display.module.less';

const itemPriceAndQuantity = (layout, item) => {
  const { unitPrice, totalPrice } = item;
  const quantityOrdered = get(item, 'quantityOrdered', 0);
  const quantityWrapperClassnames = classnames(style['quantity-wrapper'], {
    [style['greater-than-two']]: quantityOrdered > 1,
    [style['has-four-digits']]: quantityOrdered > 999,
  });
  return (
    <div className={style['item-price-quantity']}>
      {layout !== 'small' && <ItemDisplayPriceWrapper totalPrice={totalPrice} unitPrice={unitPrice} />}
      <div className={quantityWrapperClassnames}>
        <div className={style['quantity-content']}>
          <div className={style['quantity-title']}>
            <FormattedMessage defaultMessage='qty' description='itemDisplay.quantityAbbreviation' id='U7ldNS' />
          </div>
          <div className={style.quantity}>
            <FormattedNumber value={quantityOrdered} />
          </div>
        </div>
      </div>
    </div>
  );
};

const ItemDisplay = ({
  layout = 'regular',
  showImage = true,
  item = null,
  onNameClicked = noop,
  className = '',
  editableReturnReason = false,
  onReturnReasonChange = noop,
  disableReturnReason = false,
}) => {
  const { isFeatureEnabled: isEditProductEnabled, disabledMessage: productLinkDisabledMessage } = useProductCatalogFeatureGate();

  if (!item) {
    return null;
  }
  const { productId, name, sku, returnReasonId, attributes, bundleProductId, bundleProductSku, productThumbnailUrl } = item;
  return (
    <div
      className={classnames(style['item-display'], style[layout], className, {
        [style['no-image']]: !showImage,
        [style['no-attributes']]: !attributes,
      })}
    >
      {showImage && <ItemDisplayImage name={name} productThumbnailUrl={productThumbnailUrl} />}
      <div className={style['non-image-content']}>
        <div className={style['text-content']}>
          <Button
            className={style['item-name']}
            disabled={!isEditProductEnabled}
            disabledTooltipMessage={<Text textType={TextType.Paragraph} value={productLinkDisabledMessage} />}
            onClick={() => onNameClicked(productId)}
            theme='link'
          >
            {name}
          </Button>
          <div className={style['item-sku']}>{sku}</div>
          {bundleProductId && <div className={style['item-sku']}>{bundleProductSku}</div>}
          <ItemDisplayDescriptions descriptions={attributes} />
          {returnReasonId && !editableReturnReason && (
            <div className={style['return-reason']}>{ReturnReasonService.getReturnReasonText({ returnReasonId })}</div>
          )}
          {editableReturnReason && (
            <div className={style['return-reason']}>
              <ReturnReasonDropdown
                disabled={disableReturnReason}
                onChange={(newReason) => onReturnReasonChange(newReason)}
                selectedReturnReasonId={returnReasonId || ''}
                theme='link'
              />
            </div>
          )}
        </div>
        {layout !== 'hidePriceAndQuantity' && itemPriceAndQuantity(layout, item)}
      </div>
    </div>
  );
};

ItemDisplay.propTypes = {
  layout: PropTypes.oneOf(['regular', 'small', 'hidePriceAndQuantity']),
  showImage: PropTypes.bool,
  onNameClicked: PropTypes.func,
  item: OrderItemPropType,
  className: PropTypes.string,
  editableReturnReason: PropTypes.bool,
  onReturnReasonChange: PropTypes.func,
  disableReturnReason: PropTypes.bool,
};

export default ItemDisplay;
